import React, { forwardRef } from 'react'
import styled, { css } from 'styled-components'
import Typography, { TYPOGRAPHY_ICON_SIZE_CSS_VAR, TYPOGRAPHY_LINE_HEIGHT_CSS_VAR } from './Typography'
import cn from 'clsx'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { setCSSVar } from 'lib/theme/themeUtils'

export const CaptionLarge = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '18px')}
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(16))}
  font-size: 12px;

  ${mediaQueryUp.tablet} {
    font-size: 13px;
  }
`

export const CaptionMedium = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '14px')}
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(16))}
  font-size: 11px;

  ${mediaQueryUp.tablet} {
    ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '16px')}
    font-size: 12px;
  }
`

export const CaptionSmall = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '11px')}
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(16))}
  font-size: 10px;
  font-weight: ${props => props.theme.font.primary.weight.bold};
  letter-spacing: 0.7px;
`

/*
  Font sizes are express in px as they are not to be affected by the
  20% REM font decrease at mobile sizes. These font sizes are absolute sizes.
*/
const Element = styled(Typography)`
  font-family: ${props => props.theme.font.primary.family};

  &.variant-large {
    ${CaptionLarge}
  }

  &.variant-large:is(small),
  &.variant-large small,
  &.variant-medium {
    ${CaptionMedium}
  }

  &.variant-medium:is(small),
  &.variant-medium small,
  &.variant-small,
  &.variant-small:is(small),
  &.variant-small small {
    ${CaptionSmall}
  }

  &.weight-normal { font-weight: ${props => props.theme.font.primary.weight.regular}; }
  &.weight-bold, &:is(b, strong), b, strong { font-weight: ${props => props.theme.font.primary.weight.bold}; }
  &.italics, &:is(i, em), i, em { font-style: italic; }
  &.underline { text-decoration: underline; }
  &.strike-through, &:is(s), s { text-decoration: line-through; }
`

interface Props extends Omit<React.ComponentProps<typeof Typography>, 'forwardedAs'> {
  variant: 'small' | 'medium' | 'large';
  weight?: 'normal' | 'bold';
  italics?: boolean;
  underline?: boolean;
  strikeThrough?: boolean;
}

const Caption = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    variant,
    weight,
    italics,
    underline,
    strikeThrough,
    as,
    className,
    ...rest
  } = props

  return <Element
    {...rest}
    ref={ref}
    forwardedAs={as}
    className={cn(
      className,
        `variant-${variant}`,
        weight ? `weight-${weight}` : undefined,
        {
          italics,
          underline,
          'strike-through': strikeThrough,
        },
    )}
  />
})

Caption.displayName = 'Caption'

export default Caption
